import React, { useState, useEffect } from "react";
import { useWallet } from "../hooks/useWallet";
import { useKatpackStore } from "../stores/useKatpackStore";
import { AgentAvatar, PALETTES, parseSeed } from "./AgentAvatar";
import { ClaimAgentModal } from "./ClaimAgentModal";

const PHASE = {
  DISCONNECTED: "disconnected", // no agents, no katbot token
  PARTIAL: "partial",           // agents loaded from DB, katbot token absent (e.g. after refresh)
  SIGNING: "signing",
  CONNECTING: "connecting",
  CONNECTED: "connected",       // agents + portfolios loaded, katbot token present
  ERROR: "error",
};


/**
 * Modal: pick a Katbot agent to onboard from the available list.
 */
function AddAgentModal({ config, jwtToken, katbotToken, onAdded, onClose }) {
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${config.apiBaseUrl}/katbot/agents/available`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Katbot-Token": katbotToken,
          },
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail || `Failed to load agents (${resp.status})`);
        }
        setAvailableAgents(await resp.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (agent) => {
    setAdding(agent.id);
    setError(null);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/katbot/agents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
          "X-Katbot-Token": katbotToken,
        },
        body: JSON.stringify({ katbot_agent_id: agent.id, agent_name: agent.name }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to add agent (${resp.status})`);
      }
      const result = await resp.json();
      setAvailableAgents((prev) =>
        prev.map((a) =>
          a.id === agent.id ? { ...a, katpack_onboarded: true, katpack_agent_id: result.id } : a
        )
      );
      onAdded?.(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-white font-semibold">Add Katbot Agent</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-3">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
          )}
          {loading && (
            <p className="text-gray-400 text-sm text-center animate-pulse py-4">Loading your Katbot agents…</p>
          )}
          {!loading && availableAgents.length === 0 && (
            <div className="text-center py-6">
              <p className="text-gray-400 text-sm">No agents found in your Katbot account.</p>
              <p className="text-gray-500 text-xs mt-1">Create agents in Katbot first, then return here.</p>
            </div>
          )}
          {!loading && availableAgents.map((agent) => (
            <div key={agent.id} className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                {(() => { const { avatarSeed, paletteIndex } = parseSeed(agent.avatar_seed); return <AgentAvatar name={agent.name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={32} />; })()}
                <div>
                  <p className="text-white font-mono text-sm font-semibold">
                    {agent.katpack_onboarded && agent.canonical_name
                      ? agent.canonical_name
                      : agent.name}
                  </p>
                  <p className="text-gray-500 text-xs">ID {agent.id}</p>
                </div>
              </div>
              {agent.katpack_onboarded ? (
                <span className="text-xs text-green-400 bg-green-400/10 border border-green-400/20 px-2 py-1 rounded font-medium">✓ Added</span>
              ) : (
                <button
                  onClick={() => handleAdd(agent)}
                  disabled={adding === agent.id}
                  className="text-xs bg-katpack-600 hover:bg-katpack-700 disabled:bg-gray-600 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                >
                  {adding === agent.id ? "Adding…" : "Add"}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="px-6 pb-5 flex justify-end">
          <button
            onClick={onClose}
            className="text-sm text-gray-400 hover:text-white px-4 py-2 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Card for one onboarded agent, showing all portfolios where this agent
 * is the primary agent. Each eligible portfolio gets a "Create KatPack" button.
 */
function OnboardedAgentCard({ agent, portfolios, config, onCreatePack, onAgentUpdated }) {
  const { account } = useWallet();
  const [showClaim, setShowClaim] = useState(false);
  const [invalidating, setInvalidating] = useState(false);
  const claimed = agent.owner_wallet_claimed;

  const handleInvalidate = async () => {
    if (!window.confirm(`Release ownership claim for "${agent.canonical_name ?? agent.agent_name}"?`)) return;
    setInvalidating(true);
    try {
      const resp = await fetch(
        `${config.apiBaseUrl}/agents/${agent.canonical_name ?? agent.agent_name}/claim?wallet_address=${account}`,
        { method: "DELETE" }
      );
      if (resp.ok) onAgentUpdated?.({ agent_name: agent.canonical_name ?? agent.agent_name, owner_wallet_claimed: false });
    } catch { /* non-fatal */ } finally {
      setInvalidating(false);
    }
  };

  // Katbot returns active_agent: { id, name, ... } on each portfolio
  const primaryPortfolios = portfolios.filter(
    (p) => p.active_agent && p.active_agent.id === agent.katbot_agent_id
  );

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4 space-y-3">
      {/* Claim modal */}
      {showClaim && (
        <ClaimAgentModal
          config={config}
          agent={agent}
          onClose={() => setShowClaim(false)}
          onClaimed={(updated) => {
            setShowClaim(false);
            onAgentUpdated?.(updated);
          }}
        />
      )}

      {/* Agent header */}
      <div className="flex items-center gap-3">
        {(() => { const { avatarSeed, paletteIndex } = parseSeed(agent.avatar_seed); return <AgentAvatar name={agent.agent_name} seed={avatarSeed} colors={PALETTES[paletteIndex] || PALETTES[0]} size={40} />; })()}
        <div className="flex-1 min-w-0">
          <p className="text-white font-mono font-semibold text-sm">{agent.canonical_name ?? agent.agent_name}</p>
          <p className="text-gray-500 text-xs">Katbot ID {agent.katbot_agent_id}</p>
        </div>
        <div className="flex items-center gap-2">
          {claimed ? (
            <>
              <span className="text-xs text-green-400 bg-green-400/10 border border-green-400/20 px-2 py-0.5 rounded">
                ✓ Claimed
              </span>
              <button
                onClick={handleInvalidate}
                disabled={invalidating}
                className="text-xs text-red-500/60 hover:text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 disabled:opacity-40 px-2 py-0.5 rounded transition-colors"
              >
                {invalidating ? "Releasing…" : "Release Claim"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowClaim(true)}
              className="text-xs text-yellow-400 bg-yellow-400/10 border border-yellow-400/30 hover:bg-yellow-400/20 px-2 py-0.5 rounded transition-colors"
            >
              ⚠ Claim Ownership
            </button>
          )}
          <span className="text-xs text-gray-500">
            Added {new Date(agent.onboarded_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Primary portfolio pills */}
      {primaryPortfolios.length > 0 && (
        <div className="flex flex-wrap gap-2 pl-[52px]">
          {primaryPortfolios.map((p) => (
            <button
              key={String(p.id)}
              onClick={() => onCreatePack?.(p)}
              title="Create KatPack from this portfolio"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-indigo-500/10 border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 transition-colors whitespace-nowrap"
            >
              <span>{p.name}</span>
              {p.portfolio_type && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  p.portfolio_type === "HYPERLIQUID"
                    ? "bg-green-400/15 text-green-400"
                    : "bg-blue-400/15 text-blue-400"
                }`}>
                  {p.portfolio_type === "HYPERLIQUID" ? "HL" : "Paper"}
                </span>
              )}
              <span className="text-indigo-400 font-bold leading-none">+</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function KatbotOnboard({ config, onCreatePack, onAgentsLoaded }) {
  const { account, jwtToken } = useWallet();
  const katbotToken = useKatpackStore((state) => state.instance.katbotToken);
  const katbotUser = useKatpackStore((state) => state.instance.katbotUser);
  const setKatbotToken = useKatpackStore((state) => state.setKatbotToken);
  const setKatbotUser = useKatpackStore((state) => state.setKatbotUser);

  const [phase, setPhase] = useState(PHASE.DISCONNECTED);
  const [portfolios, setPortfolios] = useState([]);
  const [onboardedAgents, setOnboardedAgents] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (katbotToken) {
      // Token survived in memory (same session) — full load
      loadData(katbotToken);
    } else {
      // After refresh: load just the onboarded agents from KatPack DB
      loadOnboardedAgents();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOnboardedAgents = async () => {
    try {
      const resp = await fetch(`${config.apiBaseUrl}/katbot/agents`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      if (resp.ok) {
        const agents = await resp.json();
        setOnboardedAgents(agents);
        onAgentsLoaded?.(agents);
        setPhase((current) =>
          current === PHASE.DISCONNECTED && agents.length > 0 ? PHASE.PARTIAL : current
        );
      }
    } catch {
      // non-fatal
    }
  };

  const loadData = async (token) => {
    setPhase(PHASE.CONNECTING);
    try {
      const [portfolioResp] = await Promise.all([
        fetch(`${config.apiBaseUrl}/katbot/portfolios`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Katbot-Token": token,
          },
        }),
        loadOnboardedAgents(),
      ]);
      if (!portfolioResp.ok) {
        const body = await portfolioResp.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to load portfolios (${portfolioResp.status})`);
      }
      setPortfolios(await portfolioResp.json());
      setPhase(PHASE.CONNECTED);
    } catch (err) {
      setError(err.message);
      setPhase(PHASE.ERROR);
    }
  };

  const handleConnect = async () => {
    setPhase(PHASE.SIGNING);
    setError(null);
    try {
      const nonceResp = await fetch(
        `${config.apiBaseUrl}/katbot/nonce/${account}`,
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );
      if (!nonceResp.ok) {
        const body = await nonceResp.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to get nonce (${nonceResp.status})`);
      }
      const { nonce_message } = await nonceResp.json();
      const msgHex =
        "0x" +
        Array.from(new TextEncoder().encode(nonce_message))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      const signature = await window.ethereum.request({
        method: "personal_sign",
        params: [msgHex, account],
      });
      const connectResp = await fetch(`${config.apiBaseUrl}/katbot/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
        body: JSON.stringify({ wallet_address: account, signature }),
      });
      if (!connectResp.ok) {
        const body = await connectResp.json().catch(() => ({}));
        throw new Error(body.detail || `Katbot login failed (${connectResp.status})`);
      }
      const { katbot_token, katbot_user } = await connectResp.json();
      setKatbotToken(katbot_token);
      setKatbotUser(katbot_user);
      await loadData(katbot_token);
    } catch (err) {
      setError(err.code === 4001 ? "You rejected the MetaMask signature request." : err.message);
      setPhase(PHASE.ERROR);
    }
  };

  const handleAgentAdded = (newAgent) => {
    setOnboardedAgents((prev) => {
      const exists = prev.some((a) => a.katbot_agent_id === newAgent.katbot_agent_id);
      return exists
        ? prev.map((a) => (a.katbot_agent_id === newAgent.katbot_agent_id ? newAgent : a))
        : [...prev, newAgent];
    });
    if (katbotToken) loadData(katbotToken);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">My Agents</h2>
        {phase === PHASE.PARTIAL && (
          <button
            onClick={handleConnect}
            className="text-xs bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 text-orange-300 px-3 py-1.5 rounded-lg transition-colors"
          >
            Connect Katbot
          </button>
        )}
        {phase === PHASE.CONNECTED && (
          <div className="flex items-center gap-3">
            {katbotUser?.subscription && (
              <span className="text-xs text-gray-400 bg-gray-700 border border-gray-600 px-2 py-0.5 rounded">
                {katbotUser.subscription}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span className="text-xs text-green-400 font-medium">Katbot connected</span>
            </div>
            <button
              onClick={() => setShowAddAgent(true)}
              className="text-xs bg-katpack-500/20 hover:bg-katpack-500/30 border border-katpack-500/40 text-katpack-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              + Add Agent
            </button>
          </div>
        )}
      </div>

      {/* DISCONNECTED / ERROR — connect prompt */}
      {(phase === PHASE.DISCONNECTED || phase === PHASE.ERROR) && (
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-orange-400 text-lg font-bold">K</span>
            </div>
            <div>
              <h3 className="text-white font-semibold">Connect Katbot to manage agents</h3>
              <p className="text-gray-400 text-sm mt-1">
                Link your Katbot account to onboard agents. Once added, any portfolio where
                your agent is set as primary will show a <strong className="text-gray-300">Create KatPack</strong> button.
              </p>
            </div>
          </div>
          {phase === PHASE.ERROR && error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
          )}
          <button
            onClick={handleConnect}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 rounded-xl transition-colors"
          >
            Connect Katbot
          </button>
          <p className="text-center text-xs text-gray-500">
            Uses the same wallet — one MetaMask sign, no gas required.
          </p>
        </div>
      )}

      {/* SIGNING / CONNECTING */}
      {(phase === PHASE.SIGNING || phase === PHASE.CONNECTING) && (
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 text-center">
          <p className="text-gray-400 text-sm animate-pulse">
            {phase === PHASE.SIGNING ? "Waiting for MetaMask signature…" : "Connecting to Katbot…"}
          </p>
        </div>
      )}

      {/* PARTIAL — agents visible from DB, no katbot token */}
      {phase === PHASE.PARTIAL && (
        <div className="space-y-3">
          {onboardedAgents.map((agent) => (
            <OnboardedAgentCard
              key={agent.id}
              agent={agent}
              portfolios={[]}
              config={config}
              onCreatePack={onCreatePack}
              onAgentUpdated={(updated) =>
                setOnboardedAgents((prev) =>
                  prev.map((a) => (a.canonical_name === updated.agent_name ? { ...a, owner_wallet_claimed: updated.owner_wallet_claimed ?? true } : a))
                )
              }
            />
          ))}
          <div className="bg-gray-800/50 border border-dashed border-gray-600 rounded-xl px-5 py-3">
            <p className="text-gray-500 text-xs">
              Connect Katbot to load portfolios, create new KayPacks, and add more agents.
            </p>
          </div>
        </div>
      )}

      {/* CONNECTED — agent cards */}
      {phase === PHASE.CONNECTED && (
        <div className="space-y-3">
          {onboardedAgents.length === 0 ? (
            <div className="bg-gray-800 border border-dashed border-gray-600 rounded-xl p-6 text-center">
              <p className="text-gray-400 text-sm">No agents added yet.</p>
              <button
                onClick={() => setShowAddAgent(true)}
                className="mt-2 text-xs text-katpack-400 hover:text-katpack-300 underline"
              >
                Add your first agent →
              </button>
            </div>
          ) : (
            onboardedAgents.map((agent) => (
              <OnboardedAgentCard
                key={agent.id}
                agent={agent}
                portfolios={portfolios}
                config={config}
                onCreatePack={onCreatePack}
                onAgentUpdated={(updated) =>
                  setOnboardedAgents((prev) =>
                    prev.map((a) => (a.canonical_name === updated.agent_name ? { ...a, owner_wallet_claimed: updated.owner_wallet_claimed ?? true } : a))
                  )
                }
              />
            ))
          )}
        </div>
      )}

      {showAddAgent && (
        <AddAgentModal
          config={config}
          jwtToken={jwtToken}
          katbotToken={katbotToken}
          onAdded={handleAgentAdded}
          onClose={() => setShowAddAgent(false)}
        />
      )}
    </section>
  );
}
